import Axios, { type AxiosError, type AxiosRequestConfig } from "axios";
import { getApiErrorMessage, getApiErrorStatus, getApiFieldErrors } from "../lib/apiError";
import { handleUnauthorizedStatus } from "../lib/authRedirect";

export const AXIOS_INSTANCE = Axios.create({
  // Empty baseURL uses relative URLs, which go through Vite proxy in dev mode
  // In production, set VITE_API_URL to the actual API domain
  baseURL: import.meta.env.VITE_API_URL || "",
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Enable cookies for session-based auth
});

// Request interceptor
AXIOS_INSTANCE.interceptors.request.use(
  (config) => {
    // Session cookie is automatically sent by the browser with withCredentials: true
    // No need to manually add Authorization header for cookie-based auth

    // Debug: Log request details in development
    if (import.meta.env.DEV) {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor
AXIOS_INSTANCE.interceptors.response.use(
  (response) => {
    // Debug: Log successful responses in development
    if (import.meta.env.DEV) {
      console.log(
        `[API Response] ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`,
      );
    }
    return response;
  },
  async (error: AxiosError) => {
    // Don't log cancelled requests in development
    if (Axios.isCancel(error) || error.code === "ERR_CANCELED") {
      if (import.meta.env.DEV) {
        console.log(`[API Cancelled] ${error.config?.method?.toUpperCase()} ${error.config?.url}`);
      }
      return Promise.reject(error);
    }

    // Debug: Log errors in development
    if (import.meta.env.DEV) {
      console.error(
        `[API Error] ${error.config?.method?.toUpperCase()} ${error.config?.url} - ${error.response?.status || "Network Error"}`, // i18n-ignore (dev console log)
        error.response?.data,
      );
    }

    // Signed out (401) → the login page of the current area (B2B or moderator).
    handleUnauthorizedStatus(error.response?.status);
    return Promise.reject(error);
  },
);

// Custom instance for Orval
export const customInstance = <T>(config: AxiosRequestConfig): Promise<T> => {
  // In development, ignore abort signals to prevent premature cancellations
  // This helps with React Query's aggressive cancellation during component updates
  const requestConfig = import.meta.env.DEV ? { ...config, signal: undefined } : config;

  return AXIOS_INSTANCE(requestConfig).then(({ data }) => data);
};

// Type-safe error handler
export interface ApiError {
  message: string;
  code: string;
  statusCode?: number;
  errors?: Record<string, string[]>;
}

/**
 * @deprecated Prefer `getApiErrorMessage` / `getApiFieldErrors` from
 * `src/lib/apiError`. Kept for existing callers; now reads DRF's real error
 * shapes (`error`, `detail`, field errors) instead of `message`/`errors`.
 */
export const handleApiError = (error: unknown): ApiError => {
  const fieldErrors = getApiFieldErrors(error);
  const errors = Object.keys(fieldErrors).length
    ? Object.fromEntries(Object.entries(fieldErrors).map(([field, msg]) => [field, [msg]]))
    : undefined;
  const statusCode = getApiErrorStatus(error);
  return {
    message: getApiErrorMessage(error),
    code: statusCode === undefined ? "NETWORK_ERROR" : "API_ERROR",
    statusCode,
    errors,
  };
};
